import { useId, useState } from 'react';

import { Button } from '@ds/core';
import { FormField, Input } from '@ds/forms';
import { documentoFormatado, documentoValido, whatsappNormalizado } from '@/comuns/documento';

export interface DadosDaClinica {
    nomeClinica: string;
    nome: string;
    documento: string;
    whatsapp: string;
}

type Erros = Partial<Record<keyof DadosDaClinica, string>>;

/**
 * Passo 2: a clínica. Só o que a cobrança e o contato pedem — o resto (endereço,
 * salas, equipe) fica para Configurações. A conta é a mesma do banco (0150); o
 * servidor confere de novo.
 */
export function EtapaClinica({ email, inicial, aoContinuar, aoTrocarDeConta }: {
    email: string;
    inicial: DadosDaClinica;
    aoContinuar: (dados: DadosDaClinica) => void;
    aoTrocarDeConta: () => void;
}) {
    const [d, setD] = useState<DadosDaClinica>(inicial);
    const [erros, setErros] = useState<Erros>({});

    const idClinica = useId();
    const idNome = useId();
    const idDocumento = useId();
    const idWhatsapp = useId();

    const campo = (chave: keyof DadosDaClinica) => (valor: string) => setD((atual) => ({ ...atual, [chave]: valor }));

    const continuar = () => {
        const e: Erros = {};
        if (d.nomeClinica.trim().length < 3) e.nomeClinica = 'O nome da clínica precisa de ao menos 3 caracteres.';
        if (d.nome.trim().length < 3) e.nome = 'Informe seu nome completo.';
        if (!documentoValido(d.documento)) e.documento = 'CPF ou CNPJ inválido. Confira os números.';
        if (!whatsappNormalizado(d.whatsapp)) e.whatsapp = 'Informe o WhatsApp com DDD, por exemplo (64) 99912-3456.';
        setErros(e);
        if (Object.keys(e).length > 0) return;
        aoContinuar({ ...d, nomeClinica: d.nomeClinica.trim(), nome: d.nome.trim(), documento: documentoFormatado(d.documento) });
    };

    return (
        <form onSubmit={(e) => { e.preventDefault(); continuar(); }} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <h2 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 600, color: 'var(--text-primary)' }}>Sua clínica</h2>
                <p style={{ margin: 0, fontSize: 12.5, lineHeight: '18px', color: 'var(--text-secondary)' }}>
                    Entrou como <strong>{email}</strong>.{' '}
                    <button type="button" onClick={aoTrocarDeConta} style={{ border: 0, padding: 0, background: 'transparent', cursor: 'pointer', font: 'inherit', color: 'var(--text-brand)', fontWeight: 600 }}>
                        Usar outra conta
                    </button>
                </p>
            </div>

            <FormField label="Nome da clínica" htmlFor={idClinica} error={erros.nomeClinica} required>
                <Input id={idClinica} name="nomeClinica" autoComplete="organization" value={d.nomeClinica} onChange={campo('nomeClinica')} invalid={!!erros.nomeClinica} />
            </FormField>

            <FormField label="Seu nome" htmlFor={idNome} error={erros.nome} required>
                <Input id={idNome} name="nome" autoComplete="name" value={d.nome} onChange={campo('nome')} invalid={!!erros.nome} />
            </FormField>

            <FormField label="CPF ou CNPJ" htmlFor={idDocumento} hint="O mesmo documento vai para a cobrança." error={erros.documento} required>
                <Input id={idDocumento} name="documento" value={d.documento} onChange={campo('documento')} invalid={!!erros.documento} />
            </FormField>

            <FormField label="WhatsApp" htmlFor={idWhatsapp} hint="Com DDD. É por ele que a equipe da Fórmula MED fala com você." error={erros.whatsapp} required>
                <Input id={idWhatsapp} type="tel" name="whatsapp" autoComplete="tel-national" inputMode="tel" value={d.whatsapp} onChange={campo('whatsapp')} placeholder="(64) 99912-3456" invalid={!!erros.whatsapp} />
            </FormField>

            <Button type="submit" fullWidth>Escolher o plano</Button>
        </form>
    );
}
